import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'react-bootstrap'
import CalcSection from './CalcSection'
import CalcParagraph from './CalcParagraph'
import NavBtn from './NavBtn'
import NavBar from './NavBar'
import PopOver from './PopOver'
import * as forms from '../forms/form'
import FormattedConnectedInput from '../forms/FormattedConnectedInput'
import ALink from './ALink'
import { mailToEmailAddr } from '../social.js'
import HelpFilter from './HelpFilter'

/**
 *
 * Basic component for rendering Section 6 of the calculator
 */
export default class Section6 extends Component {
  /**
   * Render component
   */
  render () {
    return (
      <CalcSection title={this.props.title}>
        <CalcParagraph>
          <HelpFilter showLevel={[1, 2]}>
            There are two fundamental ways to cover monthly costs in retirement:
            to have a source of income and to use up assets.
            <HelpFilter showLevel={[1]}>
              Income and assets can come in many forms and a general example of
              using assets is simply to take money out of a savings account
              where the money in the savings account is an "asset".
            </HelpFilter>
          </HelpFilter>{' '}
          We'll cover income in the next section and stick with assets for now.
        </CalcParagraph>
        <CalcParagraph>
          <HelpFilter showLevel={[1]}>
            Typically in the U.S. and Canada, retirement savings will be held in
            a tax-deferred or tax-free account (or both). "Tax-deferred" means
            taxes have not been paid on contributions and will be paid later
            when money is withdrawn. In the U.S. a
            <ALink href='http://www.investopedia.com/terms/t/traditionalira.asp?lgl=rira-baseline'>
              traditional IRA
            </ALink>
            and the majority of 401k plans are tax-deferred savings and in
            Canada an
            <ALink href='http://www.investopedia.com/terms/r/rrsp.asp'>
              RRSP
            </ALink>
            is the most common type of tax-deferred savings. "Tax-free" means
            that taxes have already been paid on contributions and that
            investment returns are also not taxed. In the U.S. the
            <ALink href='http://www.investopedia.com/terms/r/rothira.asp'>
              Roth IRA
            </ALink>
            is a popular tax-free account and in Canada the
            <ALink href='http://www.investopedia.com/terms/t/tax-free-savings-account-tfsa.asp'>
              tax-free savings account
            </ALink>
            is most common. If you are maxing contributions to both tax-deferred
            and tax-free accounts, first, congratulations - great work preparing
            for your future. Second, savings beyond that will most likely be
            held in an investment account subject to short or long term capital
            gains taxes. This is an example of "after-tax" savings.
          </HelpFilter>{' '}
        </CalcParagraph>
        <CalcParagraph>
          <HelpFilter showLevel={[1, 2]}>
            Figuring out how to allocate your assets across tax-deferred,
            tax-free and after-tax accounts gets complicated fast particularly
            when planning for future contributions.
          </HelpFilter>{' '}
          Because tax-deferred savings is by far the most common type, we will
          handle just that type for now to keep things simple. If you would find
          it useful to be able to enter tax-free or after-tax savings here,
          <ALink href={mailToEmailAddr} noNewTab>
            let me know!
          </ALink>{' '}
          <HelpFilter showLevel={[1, 2]}>
            If you don't know what type of account you have, call up the account
            manager (the bank, broker, etc.) and ask or look online.
          </HelpFilter>
        </CalcParagraph>
        <Form style={forms.formWrapperStyle} horizontal>
          <FormattedConnectedInput
            label={forms.taxDeferredSavingsLabel}
            reduxKey={forms.taxDeferredSavingsKey}
            dollarForm
          />
        </Form>
        <CalcParagraph>
          <HelpFilter showLevel={[1, 2]}>
            For your tax-deferred savings, we need to capture what you estimate
            the tax rate will be on the distributions. This will vary depending
            on your income in retirement and how you expect tax rates to change
            over time.
            <ALink href='http://www.investopedia.com/articles/personal-finance/061915/how-your-401k-taxed-when-you-retire.asp'>
              This article
            </ALink>
            explains that typically folks will be in the 25% to 33% range given
            the current economic and political environment.
            <HelpFilter showLevel={[1]}>
              There are a lot of unknowns to this assumption and there's not
              much that can be done about that. For those Americans making
              $100,000 a year or less it is probably as safe a guess as any to
              put 30% here and move on. One strategy to hedge against the risk
              of the unknown is to dedicate some time each year to re-run this
              calculator. Keep up on your retirement readiness on a regular
              basis and adjust the assumptions (inflation, tax rate, etc.) over
              time as things change and you'll be ready for events that can't be
              known now (like tax rate changes).
            </HelpFilter>
          </HelpFilter>
        </CalcParagraph>
        <Form style={forms.formWrapperStyle} horizontal>
          <FormattedConnectedInput
            label={forms.taxRateLabel}
            reduxKey={forms.taxRateKey}
            percentForm
          />
        </Form>
        <HelpFilter showLevel={[1, 2]}>
          <CalcParagraph>
            Next we need to understand what your expected rate of return on your
            savings will be.
            <HelpFilter showLevel={[1]}>
              This assumes that your money is earning a return which means it is
              invested. If the bulk of your retirement savings is not earning a
              return (3% minimum), that is a mistake and{' '}
              <PopOver linkText="here's why.">
                Cash under a mattress would also qualify as an asset or the
                equivalent, money sitting in a national bank savings account
                earning .002% interest. The problem is that inflation will eat
                away at the value of cash. Using the U.S. historical average of
                3%, this means that the cash under the mattress will be about 3%
                less valuable each year that it is held there. As an example,
                $5,000 sitting under a mattress for thirty years would only
                purchase about $2,000 worth of goods relative to year one. At
                the very least, any cash savings should be held in a high
                interest savings account. "High interest" has taken on a new
                meaning in the current economic environment where interest rates
                and inflation have been extremely low over the last few years.
                But generally speaking, the typical consumer bank savings
                account will not offer an interest rate that is competitive with
                a
                <ALink href='http://www.thesimpledollar.com/best-high-interest-savings-accounts/'>
                  "high yield" account.
                </ALink>
              </PopOver>
            </HelpFilter>
          </CalcParagraph>
          <CalcParagraph>
            Investing is a whole world unto itself and investing advice is far
            beyond the scope of what we're working on here.
            <HelpFilter showLevel={[1]}>
              But to come up with any realistic retirement plan you have to
              estimate what you believe your average annual returns will be.
            </HelpFilter>{' '}
            Most advice suggests that you should take more risk when you are
            younger and less risk as you get older and particularly less risk
            when you are in retirement. To support this idea, we'll capture your
            expected average annual return before retirement and what you expect
            during retirement.
          </CalcParagraph>
          <CalcParagraph>
            <HelpFilter showLevel={[1]}>
              It is common for people to honestly have no idea what they should
              expect (or are currently receiving) for an annual rate of return.
              If you fall in this category, don't worry, you aren't alone -{' '}
              <PopOver linkText='click here for an idea of how to proceed.'>
                First, if you have a retirement savings account you could always
                reach out to your account manager and simply ask what your
                annual rate of return has been. Secondly, understanding if your
                money is conservatively or aggressively invested can help you
                understand what to expect over time. Generally speaking, if you
                have a retirement account that is conservatively invested, you
                can expect somewhere around a 3-4% annual return. If you are
                aggressively invested, you can expect somewhere around 7-10%.
                These are very rough estimates and come with a boatload of
                assumptions. When in doubt, make a conservative guess as this
                will allow you more of a buffer in retirement.
              </PopOver>
            </HelpFilter>
          </CalcParagraph>
          <Form style={forms.formWrapperStyle} horizontal>
            <FormattedConnectedInput
              label={forms.preAssetsReturnLabel}
              reduxKey={forms.preAssetsReturnKey}
              percentForm
              wideLayout
            />
            <FormattedConnectedInput
              label={forms.postAssetsReturnLabel}
              reduxKey={forms.postAssetsReturnKey}
              percentForm
              wideLayout
            />
          </Form>
        </HelpFilter>
        <HelpFilter showLevel={[3]}>
          <Form style={forms.formWrapperStyle} horizontal>
            <FormattedConnectedInput
              label={forms.preAssetsReturnLabel}
              reduxKey={forms.preAssetsReturnKey}
              percentForm
            />
            <FormattedConnectedInput
              label={forms.postAssetsReturnLabel}
              reduxKey={forms.postAssetsReturnKey}
              percentForm
            />
          </Form>
        </HelpFilter>
        <NavBar>
          <NavBtn nextSection={5} back />
          <NavBtn nextSection={7} />
        </NavBar>
      </CalcSection>
    )
  }
}

CalcSection.propTypes = {
  title: PropTypes.string.isRequired
}

/* stashed content

This is an important aspect of calculating how
          much your savings will contribute to your monthly costs in retirement.{' '}
          <PopOver linkText='Here is a simple example to show why.'>
            If your monthly costs are $3,500 then to cover one year of expenses
            you would need $42,000 in a tax-free Roth IRA or savings account to
            cover those costs. However, if you were pulling money out of a
            tax-deferred account like a 401k and the tax rate is 30% (not
            unlikely... more on this later) then you would need 30% more money
            in the account to cover the same costs - $54,600.
          </PopOver>

                  <CalcParagraph>
          There are several distinct advantages to tax-deferred and tax-free
          accounts and some personal preference assumptions that need to be made
          to decide which is right for you, all of which is beyond the scope of
          this tool.
          <ALink href='http://www.investopedia.com/articles/taxes/11/tax-deferred-tax-free.asp'>
            This article
          </ALink>
          does a decent job of laying out the particulars (they use the term
          "tax-exempt" as opposed to "tax-free" but it means the same thing). In
          this authors opinion, if you are contributing 50-70% (or more) of your
          max to your 401k (or another tax-deferred account) you should consider
          hedging against future tax liability by contributing some portion of
          savings to a tax-free account. At least do some research and make an
          informed decision.
        </CalcParagraph>

        <FormattedConnectedInput
            label={forms.taxFreeSavingsLabel}
            reduxKey={forms.taxFreeSavingsKey}
            dollarForm
          />
 */



// WEBPACK FOOTER //
// ./src/components/sections/Section6Savings.js